<template>
  <div class="dashboard-container">

    <div class="dashboard-header">
      <h1 class="dashboard-title">Dashboard</h1>
      <div class="header-actions">
        <button class="refresh-button" @click="refreshData" title="Daten aktualisieren">
          <i class="fas fa-sync-alt"></i>
        </button>
      </div>
    </div>
    
    <div class="dashboard-layout">
      <div class="dashboard-main">
        <!-- Statistik-Karten -->
        <div class="stats-grid">
          <!-- Tests-Karte -->
          <div class="glass-card primary-card">
            <div class="card-icon">
              <i class="fas fa-clipboard-check"></i>
            </div>
            <div class="card-content">
              <span class="card-label">Tests</span>
              <div class="card-value">{{ totalTests }}</div>
              <p class="card-description">Erstellte Assessments</p>
            </div>
            <router-link to="/tests" class="card-action">
              <span>Tests verwalten</span>
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>
          
          <!-- Einträge-Karte -->
          <div class="glass-card secondary-card">
            <div class="card-icon">
              <i class="fas fa-file-alt"></i>
            </div>
            <div class="card-content">
              <span class="card-label">Einträge</span>
              <div class="card-value">{{ totalEntries }}</div>
              <p class="card-description">Ausgefüllte Tests</p>
            </div>
            <router-link to="/entries" class="card-action">
              <span>Einträge anzeigen</span>
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>
          
          <!-- Bewerber-Karte -->
          <div class="glass-card tertiary-card">
            <div class="card-icon">
              <i class="fas fa-users"></i>
            </div>
            <div class="card-content">
              <span class="card-label">Bewerber</span>
              <div class="card-value">{{ totalApplicants }}</div>
              <p class="card-description">Gesamt-Teilnehmer</p>
            </div>
            <router-link to="/applicants" class="card-action">
              <span>Bewerber anzeigen</span>
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>
          
          <!-- Durchschnittliche Punktzahl -->
          <div class="glass-card quaternary-card">
            <div class="card-icon">
              <i class="fas fa-chart-line"></i>
            </div>
            <div class="card-content">
              <span class="card-label">Durchschnitt</span>
              <div class="card-value">{{ averageScorePercent }}</div>
              <p class="card-description">Durchschnittlich erreicht</p>
            </div>
            <router-link to="/analytics" class="card-action">
              <span>Details ansehen</span>
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>
        </div>
        
        <!-- NUR Chart zur Testnutzung -->
        <div class="analysis-section">
          <div class="section-header">
            <h2 class="section-title">Durchgeführte Tests</h2>
            <div class="section-actions">
              <router-link to="/analytics" class="view-all">
                Zur Analyse <i class="fas fa-chevron-right"></i>
              </router-link>
            </div>
          </div>
          
          <div class="glass-panel">
            <test-usage-chart :test-data="testCompletionData" />
          </div>
        </div>
      </div>
      
      <!-- Sidebar mit Schnellzugriff -->
      <div class="dashboard-sidebar">
        <!-- Schnellzugriff -->
        <div class="sidebar-section quick-actions">
          <h2 class="section-title">Schnellzugriff</h2>
          <div class="action-buttons">
            <router-link to="/tests/create" class="action-button">
              <i class="fas fa-plus"></i>
              <span>Test erstellen</span>
            </router-link>
            <router-link to="/entries/latest" class="action-button">
              <i class="fas fa-question-circle"></i>
              <span>Support</span>
            </router-link>
            <router-link to="/analytics" class="action-button">
              <i class="fas fa-chart-bar"></i>
              <span>Analysen</span>
            </router-link>
            <router-link to="/settings" class="action-button">
              <i class="fas fa-cog"></i>
              <span>Einstellungen</span>
            </router-link>
          </div>
        </div>
        
        <!-- Zuletzt eingereichte Tests -->
        <div v-if="hasEntries" class="entries-section">
          <div class="section-header">
            <h2 class="section-title">Zuletzt eingereichte Tests</h2>
            <router-link to="/entries" class="view-all">
              Alle anzeigen <i class="fas fa-chevron-right"></i>
            </router-link>
          </div>
          
          <div class="entries-list">
            <div v-for="(entry, index) in recentEntries" :key="index" class="entry-item">
              <div class="entry-details">
                <div class="entry-info">
                  <h3 class="entry-title">{{ entry.test?.name || 'Unnamed Test' }}</h3>
                  <div class="entry-meta">
                    <span class="entry-date">
                      <i class="far fa-calendar-alt me-1"></i>
                      {{ formatDate(entry.created_at) }}
                    </span>
                    <span v-if="entry.score >= 0" class="entry-score" :class="{'text-danger': entry.score_percentage < 50}">
                      <i class="fas fa-chart-line me-1"></i>
                      Score: {{ entry.score_percentage }}%
                    </span>
                    <span class="entry-applicant">
                      <i class="fas fa-user me-1"></i>
                      {{ entry.applicant?.full_name || 'Unbekannter Bewerber' }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="entry-actions">
                <router-link :to="`/entries/${entry.id}`" class="entry-action">
                  <span>Details</span>
                  <i class="fas fa-arrow-right"></i>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Ladezustand -->
    <div v-if="loading" class="loading-overlay">
      <div class="loader">
        <svg class="spinner" viewBox="0 0 50 50">
          <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="3"></circle>
        </svg>
        <span class="loading-text">Daten werden geladen...</span>
      </div>
    </div>
    
    <!-- Fehlerhinweis -->
    <div v-if="error" class="error-message">
      <div class="error-icon">
        <i class="fas fa-exclamation-triangle"></i>
      </div>
      <div class="error-content">
        <h3>Ein Fehler ist aufgetreten</h3>
        <p>{{ error }}</p>
        <button class="retry-button" @click="refreshData">
          <i class="fas fa-redo-alt me-2"></i>
          Erneut versuchen
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import TestUsageChart from '@/components/charts/TestUsageChart.vue';
import { useAuthStore } from '@/stores/auth';
import { useCompanyStore } from '@/stores/company';
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import http from '@/services/http';

export default {
  name: 'CompanyDashboard',
  components: {
    TestUsageChart
  },
  setup() {
    const authStore = useAuthStore();
    const companyStore = useCompanyStore();
    const toast = useToast();
    
    const loading = ref(false);
    const error = ref(null);
    const dashboardData = ref({
      totalTests: 0,
      totalEntries: 0,
      totalApplicants: 0,
      averageScore: null,
      testCompletionData: [],
      recentEntries: []
    });
    
    // Computed properties
    const totalTests = computed(() => dashboardData.value.totalTests || 0);
    const totalEntries = computed(() => dashboardData.value.totalEntries || 0);
    const totalApplicants = computed(() => dashboardData.value.totalApplicants || 0);
    const averageScore = computed(() => {
      const avg = dashboardData.value.averageScore;
      return avg ? avg.toFixed(1) : 'N/A';
    });
    const testCompletionData = computed(() => dashboardData.value.testCompletionData || []);
    const recentEntries = computed(() => dashboardData.value.recentEntries || []);
    const hasEntries = computed(() => recentEntries.value && recentEntries.value.length > 0);
    const averageScorePercent = computed(() => {
      const avg = dashboardData.value.averageScore;
      if (!avg && avg !== 0) return 'N/A';
      
      return `${Math.round(avg)}%`;
    });
    
    // Methods
    const loadCompanyData = async () => {
      try {
        // Lade Grundinformationen zur Firma (für Header, usw.)
        await companyStore.fetchOwnCompany();
      } catch (err) {
        console.error('Fehler beim Laden der Firmendaten:', err);
        error.value = 'Die Firmendaten konnten nicht geladen werden.';
      }
    };
    
    const refreshData = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        // Verwende die Store-Methode statt direktem API-Aufruf, um die Impersonierung zu unterstützen
        const data = await companyStore.fetchDashboardData();
        
        // Extrahiere die Statistikdaten
        dashboardData.value = {
          totalTests: data.statistics.totalTests || 0,
          totalEntries: data.statistics.totalEntries || 0,
          totalApplicants: data.statistics.totalApplicants || 0,
          averageScore: data.statistics.averageScore,
          testCompletionData: data.statistics.testCompletionData || [],
          recentEntries: data.latestEntries || []
        };
      
        
      } catch (err) {
        console.error('Fehler beim Aktualisieren des Dashboards:', err);
        error.value = 'Die Dashboard-Daten konnten nicht aktualisiert werden.';
        
        // Prüfen, ob der Benutzer ein Super-Admin ist, der impersoniert
        const user = authStore.user;
        const isImpersonating = authStore.isImpersonating;
        
        if (isImpersonating && user) {
          // Keine aktualisierungsfehler anzeigen während der Impersonierung
          error.value = null;
          dashboardData.value = {
            totalTests: 0,
            totalEntries: 0,
            totalApplicants: 0,
            averageScore: 0,
            testCompletionData: [],
            recentEntries: []
          };
        } else {
          toast.error('Fehler beim Aktualisieren des Dashboards');
        }
      } finally {
        loading.value = false;
      }
    };
    
    const formatDate = (dateString) => {
      if (!dateString) return 'Unbekannt';
      
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };
    
    // Methoden zum Löschen von Einträgen
    const showDirectDelete = (entry) => {
      console.log('showDirectDelete aufgerufen mit:', entry);
      entry.deleteConfirmation = true;
    };
    
    const directConfirmDelete = async (entry) => {
      if (!entry) return;
      
      try {
        await http.delete(`/api/entries/${entry.id}`);
        
        // Eintrag aus der Liste entfernen
        dashboardData.value.recentEntries = dashboardData.value.recentEntries.filter(
          e => e.id !== entry.id
        );
        
        // Statistik aktualisieren
        dashboardData.value.totalEntries--;
        
        // Erfolgsmeldung anzeigen
        toast.success('Eintrag erfolgreich gelöscht');
        
        // Dashboard neu laden, um alle Daten zu aktualisieren
        setTimeout(() => refreshData(), 500);
      } catch (err) {
        console.error('Fehler beim Löschen des Eintrags:', err);
        toast.error('Fehler beim Löschen des Eintrags');
      } finally {
        entry.deleteConfirmation = false;
      }
    };
    
    const cancelDirectDelete = (entry) => {
      entry.deleteConfirmation = false;
    };
    
    // Lifecycle hooks
    onMounted(() => {
      loadCompanyData();
      refreshData();
    });
    
    return {
      loading,
      error,
      dashboardData,
      totalTests,
      totalEntries,
      totalApplicants,
      averageScore,
      testCompletionData,
      recentEntries,
      hasEntries,
      averageScorePercent,
      loadCompanyData,
      refreshData,
      formatDate,
      showDirectDelete,
      directConfirmDelete,
      cancelDirectDelete
    };
  }
};
</script>

<style lang="scss" scoped>
@use "sass:color";

.dashboard-container {
  padding: 1.5rem;
  
  .dashboard-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    
    .dashboard-title {
      font-size: 1.75rem;
      font-weight: 600;
      color: var(--dark-color);
      margin: 0;
    }
    
    .header-actions {
      .refresh-button {
        background-color: var(--primary-color);
        color: white;
        border: none;
        border-radius: 8px;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        
        &:hover {
          background-color: color.adjust(#007bff, $lightness: -10%);
          transform: rotate(15deg);
        }
        
        i {
          font-size: 1rem;
        }
      }
      
      .test-modal-button {
        background-color: #6c757d;
        color: white;
        border: none;
        border-radius: 8px;
        margin-left: 10px;
        padding: 0.5rem 1rem;
        font-size: 0.875rem;
        cursor: pointer;
        transition: all 0.3s ease;
        
        &:hover {
          background-color: darken(#6c757d, 10%);
        }
      }
    }
  }
  
  .dashboard-layout {
    display: grid;
    grid-template-columns: 3fr 1fr;
    gap: 1.5rem;
    
    @media (max-width: 992px) {
      grid-template-columns: 1fr;
    }
    
    .dashboard-main {
      .stats-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        gap: 1.5rem;
        margin-bottom: 2rem;
        
        .glass-card {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          
          &:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
          }
          
          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 5px;
          }
          
          &.primary-card::before {
            background: linear-gradient(to right, var(--primary-color), color.adjust(#007bff, $lightness: 10%));
          }
          
          &.secondary-card::before {
            background: linear-gradient(to right, #6c757d, #adb5bd);
          }
          
          &.tertiary-card::before {
            background: linear-gradient(to right, #fd7e14, #ffc107);
          }
          
          &.quaternary-card::before {
            background: linear-gradient(to right, #20c997, #0dcaf0);
          }
          
          .card-icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 1rem;
            
            i {
              font-size: 1.5rem;
              color: white;
            }
          }
          
          &.primary-card .card-icon {
            background: linear-gradient(135deg, var(--primary-color), color.adjust(#007bff, $lightness: 10%));
          }
          
          &.secondary-card .card-icon {
            background: linear-gradient(135deg, #6c757d, #adb5bd);
          }
          
          &.tertiary-card .card-icon {
            background: linear-gradient(135deg, #fd7e14, #ffc107);
          }
          
          &.quaternary-card .card-icon {
            background: linear-gradient(135deg, #20c997, #0dcaf0);
          }
          
          .card-content {
            flex: 1;
            
            .card-label {
              display: block;
              font-size: 0.875rem;
              font-weight: 500;
              color: #6c757d;
              margin-bottom: 0.25rem;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            
            .card-value {
              font-size: 2rem;
              font-weight: 700;
              color: #212529;
              margin-bottom: 0.5rem;
            }
            
            .card-description {
              font-size: 0.875rem;
              color: #6c757d;
              margin-bottom: 1rem;
            }
          }
          
          .card-action {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding-top: 1rem;
            border-top: 1px solid rgba(0, 0, 0, 0.05);
            color: var(--primary-color);
            text-decoration: none;
            font-weight: 500;
            transition: color 0.3s ease;
            
            &:hover {
              color: color.adjust(#007bff, $lightness: -10%);
            }
            
            i {
              transition: transform 0.3s ease;
            }
            
            &:hover i {
              transform: translateX(4px);
            }
          }
        }
      }
      
      .analysis-section, .entries-section {
        background: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(10px);
        border-radius: 16px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        padding: 1.5rem;
        margin-bottom: 1.5rem;
        
        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
          
          .section-title {
            font-size: 1.25rem;
            font-weight: 600;
            color: #212529;
            margin: 0;
          }
          
          .view-all {
            color: var(--primary-color);
            text-decoration: none;
            font-weight: 500;
            display: flex;
            align-items: center;
            
            i {
              margin-left: 0.5rem;
              transition: transform 0.3s ease;
            }
            
            &:hover i {
              transform: translateX(4px);
            }
          }
        }
        
        .glass-panel {
          background: rgba(255, 255, 255, 0.6);
          border-radius: 12px;
          padding: 1rem;
          min-height: 300px;
        }
      }
    }
    
    .dashboard-sidebar {
      .sidebar-section {
        background: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(10px);
        border-radius: 16px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        padding: 1.5rem;
        margin-bottom: 1.5rem;
        
        .section-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #212529;
          margin: 0 0 1.5rem 0;
        }
        
        &.quick-actions {
          .action-buttons {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
            
            .action-button {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              text-decoration: none;
              padding: 1.25rem 0.75rem;
              background: rgba(255, 255, 255, 0.6);
              border-radius: 12px;
              color: #495057;
              transition: all 0.3s ease;
              text-align: center;
              
              i {
                font-size: 1.5rem;
                margin-bottom: 0.75rem;
                color: var(--primary-color);
                transition: transform 0.3s ease;
              }
              
              span {
                font-size: 0.875rem;
                font-weight: 500;
              }
              
              &:hover {
                background: rgba(255, 255, 255, 0.9);
                transform: translateY(-3px);
                box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
                
                i {
                  transform: scale(1.1);
                }
              }
            }
          }
        }
        
        .entries-list {
          .entry-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 1rem;
            background: rgba(255, 255, 255, 0.6);
            border-radius: 12px;
            margin-bottom: 1rem;
            transition: all 0.3s ease;
            
            &:last-child {
              margin-bottom: 0;
            }
            
            &:hover {
              background: rgba(255, 255, 255, 0.9);
              transform: translateY(-3px);
              box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
            }
            
            .entry-details {
              flex: 1;
              
              .entry-info {
                .entry-title {
                  font-size: 1rem;
                  font-weight: 600;
                  color: #212529;
                  margin: 0 0 0.5rem 0;
                }
                
                .entry-meta {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 1rem;
                  
                  span {
                    font-size: 0.75rem;
                    color: #6c757d;
                    display: flex;
                    align-items: center;
                    
                    i {
                      margin-right: 0.25rem;
                    }
                    
                    &.entry-score {
                      color: var(--success-color);
                      font-weight: 600;
                    }
                  }
                }
              }
            }
            
            .entry-actions {
              display: flex;
              align-items: center;
              color: var(--primary-color);
              text-decoration: none;
              font-weight: 500;
              font-size: 0.875rem;
              
              i {
                margin-left: 0.5rem;
                transition: transform 0.3s ease;
              }
              
              &:hover i {
                transform: translateX(4px);
              }
            }
          }
        }
      }
    }
  }
  
  .loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(5px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    
    .loader {
      display: flex;
      flex-direction: column;
      align-items: center;
      
      .spinner {
        width: 50px;
        height: 50px;
        animation: spinner-rotate 2s linear infinite;
        
        .path {
          stroke: var(--primary-color);
          stroke-linecap: round;
          animation: spinner-dash 1.5s ease-in-out infinite;
        }
      }
      
      .loading-text {
        margin-top: 1rem;
        font-size: 1rem;
        color: #212529;
      }
    }
  }
  
  .error-message {
    background: rgba(255, 236, 236, 0.9);
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(255, 71, 71, 0.2);
    padding: 1.5rem;
    display: flex;
    align-items: flex-start;
    margin: 2rem 0;
    
    .error-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: rgba(255, 71, 71, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 1rem;
      flex-shrink: 0;
      
      i {
        color: #ff4747;
        font-size: 1.5rem;
      }
    }
    
    .error-content {
      flex: 1;
      
      h3 {
        font-size: 1.25rem;
        font-weight: 600;
        color: #dc3545;
        margin: 0 0 0.5rem 0;
      }
      
      p {
        font-size: 0.95rem;
        color: #6c757d;
        margin: 0 0 1rem 0;
      }
      
      .retry-button {
        background: #dc3545;
        color: white;
        border: none;
        border-radius: 8px;
        padding: 0.5rem 1rem;
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        transition: background 0.3s ease;
        
        &:hover {
          background: color.adjust(#dc3545, $lightness: -10%);
        }
      }
    }
  }
}

@keyframes spinner-rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes spinner-dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

.text-danger {
  color: #dc3545 !important;
}

.entry-item {
  .delete-button {
    background: none;
    border: none;
    color: #dc3545;
    margin-left: 0.5rem;
    padding: 0.25rem;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.7;
    
    &:hover {
      opacity: 1;
      transform: scale(1.1);
    }
    
    i {
      font-size: 0.9rem;
    }
  }
  
  .entry-action {
    display: flex;
    align-items: center;
  }
}
</style> 